import * as React from "react"
import { router } from "@inertiajs/react"
import { ChevronDown, ChevronUp } from "lucide-react"
import { Switch } from "@/components/ui/switch"

export function SectionShell({
  id,
  title,
  toggle,
  move,
  children,
}: {
  id: string
  title: string
  /** Green/red visibility toggle — only registry entries backed by a
   *  PageSection row (fields / feature lists) can be hidden. */
  toggle?: { slug: string; visible: boolean }
  /** Up/down arrows — same idiom as the ranked photo slots. Present only on
   *  a page whose public component honours the order (PageSectionOrder). */
  move?: { up?: () => void; down?: () => void }
  children: React.ReactNode
}) {
  return (
    <section id={id} className="border-b border-hairline pb-12 pt-8 first:pt-0">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {move && (
            <div className="flex gap-1">
              <button
                type="button"
                onClick={move.up}
                disabled={!move.up}
                className="rounded p-1 text-ink-muted hover:bg-surface hover:text-ink-display disabled:opacity-30"
                aria-label={`Move ${title} up`}
              >
                <ChevronUp className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={move.down}
                disabled={!move.down}
                className="rounded p-1 text-ink-muted hover:bg-surface hover:text-ink-display disabled:opacity-30"
                aria-label={`Move ${title} down`}
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
          )}
          <h2>{title}</h2>
        </div>
        {toggle && (
          <Switch
            size="sm"
            checked={toggle.visible}
            onChange={(visible) =>
              router.patch(
                `/admin/pages/sections/${encodeURIComponent(toggle.slug)}/visibility`,
                { visible },
                { preserveScroll: true, preserveState: true }
              )
            }
            label={toggle.visible ? "On the live page" : "Hidden"}
          />
        )}
      </div>
      {toggle && !toggle.visible && (
        <p className="mb-4 text-sm text-ink-muted">
          Hidden from the live page — the copy below is kept and comes back the moment this is
          switched on.
        </p>
      )}
      {children}
    </section>
  )
}
